import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Trash2, Shield, User, Loader2, Pencil, KeyRound, X } from "lucide-react";

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "presenter" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // user being edited
  const [editDraft, setEditDraft] = useState({ name: "", email: "", role: "presenter" });
  const [resetting, setResetting] = useState(null); // user getting password reset
  const [resetDraft, setResetDraft] = useState("");

  const openEdit = (u) => {
    setEditing(u);
    setEditDraft({ name: u.name, email: u.email, role: u.role });
  };
  const openReset = (u) => {
    setResetting(u);
    setResetDraft("");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/users/${editing.id}`, editDraft);
      toast.success("Account updated");
      if (user?.id === editing.id) checkAuth();
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const saveReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/users/${resetting.id}/reset-password`, { password: resetDraft });
      toast.success(`Password reset for ${resetting.email}`);
      setResetting(null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setUsers([]);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Account created");
      setForm({ email: "", password: "", name: "", role: "presenter" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/app")} className="p-2 rounded-lg hover:bg-slate-100 transition-all">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="font-display font-bold text-lg text-slate-900">Manage presenter accounts</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 grid md:grid-cols-5 gap-8">
        <form onSubmit={create} className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4 h-fit" data-testid="create-user-form">
          <h2 className="font-display font-bold text-slate-900 flex items-center gap-2"><UserPlus className="w-5 h-5 text-indigo-600" /> New account</h2>
          <input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="Name" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm" />
          <input data-testid="new-user-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="Email" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm" />
          <input data-testid="new-user-password" type="text" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                 placeholder="Password" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm" />
          <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm bg-white">
            <option value="presenter">Presenter</option>
            <option value="admin">Admin</option>
          </select>
          <button data-testid="create-user-submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl px-4 py-2.5 transition-all disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create account
          </button>
        </form>

        <div className="md:col-span-3 space-y-3">
          {users === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
          ) : users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm">
              <span className={`w-9 h-9 rounded-full flex items-center justify-center ${u.role === "admin" ? "bg-violet-100 text-violet-700" : "bg-indigo-100 text-indigo-700"}`}>
                {u.role === "admin" ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{u.name}</div>
                <div className="text-sm text-slate-500 truncate">{u.email}</div>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{u.role}</span>
              <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} title="Edit profile"
                      className="p-2 rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-all">
                <Pencil className="w-4 h-4" />
              </button>
              <button data-testid={`reset-password-${u.id}`} onClick={() => openReset(u)} title="Reset password"
                      className="p-2 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-all">
                <KeyRound className="w-4 h-4" />
              </button>
              <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} title="Delete"
                      className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-all">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </main>

      {/* Edit profile modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl" data-testid="edit-user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Edit profile</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-4">
              <input data-testid="edit-name-input" value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                     placeholder="Name" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm" />
              <input data-testid="edit-email-input" type="email" required value={editDraft.email} onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })}
                     placeholder="Email" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm" />
              <select data-testid="edit-role-select" value={editDraft.role} onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm bg-white">
                <option value="presenter">Presenter</option>
                <option value="admin">Admin</option>
              </select>
              <button data-testid="edit-save-button" type="submit"
                      className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl px-4 py-2.5 transition-all">
                <Pencil className="w-4 h-4" /> Save changes
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetting && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setResetting(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl" data-testid="reset-password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Reset password</h3>
              <button onClick={() => setResetting(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Set a new password for <span className="font-semibold text-slate-800">{resetting.email}</span>. Min 8 characters with a letter and a number. Any lockout is cleared.</p>
            <form onSubmit={saveReset} className="space-y-4">
              <input data-testid="reset-password-input" type="text" required value={resetDraft} onChange={(e) => setResetDraft(e.target.value)}
                     placeholder="New password" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none text-sm" />
              <button data-testid="reset-submit-button" type="submit"
                      className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl px-4 py-2.5 transition-all">
                <KeyRound className="w-4 h-4" /> Reset password
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
